/**
 * Utility functions for handling images
 */

/**
 * Generates a placeholder image URL for a collection
 * @param collectionName The name of the collection
 * @param type The type of image (cube or avatar)
 * @returns A placeholder image URL
 */
export function generatePlaceholderImage(collectionName: string, type: "cube" | "avatar" = "cube"): string {
  const query = type === "cube" ? `${collectionName} NFT 3D cube` : `${collectionName} NFT avatar`

  const size = type === "cube" ? 240 : 120

  return `/placeholder.svg?height=${size}&width=${size}&query=${encodeURIComponent(query)}`
}

/**
 * Attempts to load an image with different extensions
 * @param basePath The base path of the image without extension
 * @param extensions Array of extensions to try
 * @returns Promise that resolves to the first successful image path or null if all fail
 */
export function tryLoadImageWithExtensions(
  basePath: string,
  extensions: string[] = [".webp", ".png", ".jpg", ".jpeg", ".gif"],
): Promise<string | null> {
  return new Promise((resolve) => {
    let extensionIndex = 0

    const tryNextExtension = () => {
      if (extensionIndex >= extensions.length) {
        resolve(null) // All extensions failed
        return
      }

      const extension = extensions[extensionIndex]
      const path = `${basePath}${extension}`
      const img = new Image()

      img.onload = () => {
        resolve(path) // This extension worked
      }

      img.onerror = () => {
        extensionIndex++
        tryNextExtension() // Try next extension
      }

      img.src = path
    }

    tryNextExtension()
  })
}
